import React from 'react';
import { Box, Typography } from '@mui/material';
import { LocationOff as LocationOffIcon } from '@mui/icons-material';
import MapZoomWrapper from './MapZoomWrapper';
import { Vyjezd2026CasovkaOverlay } from './Vyjezd2026CasovkaOverlay';
import type { Udalost } from '../api/JsdhNotifikaceService/generated/models/Udalost';
import type { MapConfig, MapaLayout } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';
import type { UdalostRouteResponse } from '../api/JsdhNotifikaceService/generated/models/UdalostRoute';

interface Vyjezd2026MapaSectionProps {
    aktivniPoplach: Udalost;
    mapConfig?: MapConfig;
    mapaLayout: MapaLayout;
    routeData: UdalostRouteResponse | null;
    stationGps?: string;
    casVyjezdMinuty: number;
    /** Překryv časovky přes mapu. Na výšku false: časovka je v hlavičce. */
    casovkaOverlay?: boolean;
}

const efektivniLayout = (mapaLayout: MapaLayout, routeData: UdalostRouteResponse | null): MapaLayout => {
    const hasRoute = !!routeData?.polyline && routeData.polyline.length > 0;
    if ((mapaLayout === 'misto-trasa' || mapaLayout === 'trasa') && !hasRoute) return 'misto';
    return mapaLayout;
};

/** Vykreslí se mapa (ne placeholder)? Používá i výpočet výšek kiosku na výšku. */
export const mapaJeViditelna = (aktivniPoplach: Udalost, mapaLayout: MapaLayout, routeData: UdalostRouteResponse | null): boolean =>
    efektivniLayout(mapaLayout, routeData) !== 'off' && !!aktivniPoplach.gps;

export const Vyjezd2026MapaSection: React.FC<Vyjezd2026MapaSectionProps> = ({
    aktivniPoplach, mapConfig, mapaLayout, routeData, stationGps, casVyjezdMinuty, casovkaOverlay = true,
}) => {
    const hasRoute = !!routeData?.polyline && routeData.polyline.length > 0;
    const hasGps = !!aktivniPoplach.gps;
    const effective = efektivniLayout(mapaLayout, routeData);
    const popisPlaceholderu = effective === 'off' ? 'Mapa je vypnutá v konfiguraci.' : 'Pro tento poplach nejsou dostupné GPS souřadnice.';

    if (effective === 'off' || !hasGps) {
        if (!casovkaOverlay) {
            // Na výšku má placeholder jen 8 % výšky: ikona a texty v jednom řádku, bez časovky.
            return (
                <Box data-testid="mapa-placeholder" sx={{
                    flex: 1, bgcolor: 'background.default',
                    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                    gap: 2, px: 2, minHeight: 0, overflow: 'hidden',
                }}>
                    <LocationOffIcon sx={{ fontSize: 48, color: 'text.disabled', flex: 'none' }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}>
                            Mapa není k dispozici
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', color: 'text.disabled' }}>
                            {popisPlaceholderu}
                        </Typography>
                    </Box>
                </Box>
            );
        }
        return (
            <Box data-testid="mapa-placeholder" sx={{
                flex: 1, position: 'relative', bgcolor: 'background.default',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 1.5,
            }}>
                <LocationOffIcon sx={{ fontSize: 96, color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.secondary' }}>
                    Mapa není k dispozici
                </Typography>
                <Typography sx={{ fontSize: '1rem', color: 'text.disabled', textAlign: 'center', px: 2 }}>
                    {popisPlaceholderu}
                </Typography>
                {aktivniPoplach.casVyhlaseni && (
                    <Vyjezd2026CasovkaOverlay
                        casVyhlaseni={aktivniPoplach.casVyhlaseni}
                        casVyjezdMinuty={casVyjezdMinuty}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', minHeight: 0 }}>
            {(effective === 'misto' || effective === 'misto-trasa') && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <MapZoomWrapper
                        gps={aktivniPoplach.gps}
                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                        showHyndrant={aktivniPoplach.pred === 'POŽÁR'}
                        height="100%"
                        mapConfig={mapConfig}
                    />
                </Box>
            )}
            {(effective === 'trasa' || effective === 'misto-trasa') && hasRoute && (
                <Box sx={{ flex: 1, minHeight: 0, ...(effective === 'misto-trasa' && { pl: '2px' }) }}>
                    <MapZoomWrapper
                        gps={aktivniPoplach.gps}
                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                        showHyndrant={false}
                        height="100%"
                        mapConfig={mapConfig}
                        routeCoordinates={routeData?.polyline}
                        stationGps={stationGps}
                        fitToBounds={hasRoute}
                    />
                </Box>
            )}
            {casovkaOverlay && aktivniPoplach.casVyhlaseni && (
                <Vyjezd2026CasovkaOverlay
                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                    casVyjezdMinuty={casVyjezdMinuty}
                />
            )}
        </Box>
    );
};
